import { ChangeDetectionStrategy, Component } from '@angular/core';
import { Router } from '@angular/router';
import { navigateToAnotherApp } from '@common/helpers';
import { EXTERNAL_SITE_CATEGORY_BIZ_MNT, FEATURE_TO_ROUTE_MAPPING } from '@common/helpers/constants/servicing.contant';
import { MyBookingTabShow } from '@common/helpers/enums/servicing.enum';
import { ServicingFunctionIdType, ServicingPageIdType } from '@common/interfaces';
import { INITIAL_MYBOOKING_STATE } from '@common/interfaces/mybooking/mybooking.interface';
import { ServicingCommonStoreService } from '@common/services';
import { MyBookingServiceService } from '@common/services/my-booking/my-booking-service/my-booking-service.service';
import { MyBookingService } from '@common/services/my-booking/my-booking.service';
import { NextActionService } from '@common/services/next-action/next-action.service';
import { selectMyBookingState } from '@common/store/my-booking';
import { MasterJsonKeyPrefix } from '@conf/asw-master.config';
import { SERVICING_ROUTES } from '@conf/routes.config';
import { environment } from '@env/environment';
import { SupportModalBlockComponent } from '@lib/components/support-class';
import { DialogClickType } from '@lib/interfaces';
import { CommonLibService, ModalIdParts, ModalService } from '@lib/services';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { GetOrderResponseData } from 'src/sdk-servicing';
import { addLinkedPayloadParts } from '../linked-modal/add-linked/add-linked-payload.state';
import { linkedPayloadParts } from '../linked-modal/linked-modal-payload.state';
import { ModalSampleComponent } from '../modal-sample/modal-sample.component';

@Component({
  selector: 'asw-menu',
  templateUrl: './menu.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MenuComponent extends SupportModalBlockComponent {
  subPageId: string = ServicingPageIdType.MY_BOOKING;
  subFunctionId: string = ServicingFunctionIdType.BOOKING_CONFIRMATION;
  public _pnr!: GetOrderResponseData;
  _modalIdParts: ModalIdParts;
  _subscription: Subscription = new Subscription();
  storeCurrentValue = INITIAL_MYBOOKING_STATE;
  dynamicMsgKeyPrefix: string = MasterJsonKeyPrefix.DYNAMIC;
  clickType!: DialogClickType;

  constructor(
    private _common: CommonLibService,
    private _router: Router,
    private store: Store,
    private _modalService: ModalService,
    private _myBookingService: MyBookingService,
    private _myBookingServiceService: MyBookingServiceService,
    private _servicingCommonStoreService: ServicingCommonStoreService,
    private readonly _nextActionService: NextActionService
  ) {
    super(_common);
    this._modalIdParts = this._modalService.defaultIdPart(ModalSampleComponent);
    this._subscription.add(
      this._servicingCommonStoreService.getServicingCommonModel$().subscribe((servicingCommon) => {
        this._pnr = (servicingCommon.getOrder as GetOrderResponseData) ?? ({} as GetOrderResponseData);
      })
    );
    this.closeWithUrlChange(this._router);
    this._subscription.add(
      this.store.select(selectMyBookingState).subscribe((val) => {
        this.storeCurrentValue = val.model ?? INITIAL_MYBOOKING_STATE;
      })
    );
  }
  reload(): void {}
  init(): void {
    this._subscription.add(
      this._myBookingService.getAddReservation().subscribe((res) => {
        if (res) {
          this.close();
        }
      })
    );
  }
  destroy(): void {
    this._myBookingService.setAddReservation(false);
    if (this._subscription) {
      this._subscription?.unsubscribe();
    }
  }

  // (22) 予約メニューモーダル閉じるボタン押下処理
  closeModal() {
    // 1.	予約メニューモーダルを非表示にする。
    this.close();
  }

  /** (34) 空席照会 / 予約ボタン押下処理 */
  clickVacantSeatReservation() {
    // 1.	予約詳細(サービス)(S01-P033)の[全サービスの編集情報破棄処理]を行う。
    this._myBookingServiceService.onHandleCommonNo3();

    // 2.	フライト検索(R01-P010)へ遷移する。
    // reservation/flight-search
    navigateToAnotherApp(environment.spa.app.res, FEATURE_TO_ROUTE_MAPPING['R01-P010'], { CONNECTION_KIND: 'ZZZ' });
  }

  clickReceipt() {
    this._myBookingService.onHandleReceipt(this._pnr, () => this.closeModal());
  }

  /** (32) 他予約検索ボタン押下処理 */
  clickSearchByRecLoc() {
    this._myBookingService.isLoadStateClickSearchMenu = true;
    // 1.	予約詳細(サービス)(S01-P033)の[全サービスの編集情報破棄処理]を行う。
    this._myBookingServiceService.onHandleCommonNo3();
    // 2.	予約検索(S01-P010)へ遷移する。
    this._router.navigate([SERVICING_ROUTES.bookingSearch]);
  }

  /** (31) 予約一覧表示ボタン押下処理 */
  clickShowReservationList() {
    // 1.	予約詳細(サービス)(S01-P033)の[全サービスの編集情報破棄処理]を行う。
    this._myBookingServiceService.onHandleCommonNo3();
    // 2.	予約一覧(S01-P020)へ遷移する。
    this._router.navigate([SERVICING_ROUTES.bookingList]);
  }

  /** (28) サービス登録ボタン押下処理 */
  clickServiceInformation() {
    // 1.	予約詳細表示タブ変数に”service”(予約詳細(サービス))を設定する。
    this._myBookingService.tabShow = MyBookingTabShow.MyBookingTabShowEnum.Service;
    // 2.	[当画面共通処理No1.表示タブ切り替え処理]を行う。
    this._myBookingService.onHandleCommonNo1();
    // close modal after switching tab
    this.closeModal();
  }

  clickPassengerInformation() {
    this._myBookingService.onHandlePassengerInformationRegistration(this._pnr, () => this.closeModal());
  }

  /** (26) 予約の紐づけボタン押下処理 */
  clickLinkOtherReservations() {
    // 2.	PNR情報取得API応答.data.linkedOrderの件数=0の場合、関連PNR追加エリアモーダルを重ねて表示する。
    if (this._pnr?.linkedOrders?.length === 0) {
      const part = linkedPayloadParts();
      this._modalService.showSubModal(part);
      const addReservation = addLinkedPayloadParts();
      this._modalService.showSubModal(addReservation);
    }
    // 1.	関連PNRモーダル表示状態をstoreで管理し、表示する旨を通知することによって関連PNRモーダルを表示する。
    else {
      const part = linkedPayloadParts();
      this._modalService.showSubModal(part);
    }
  }

  clickCancel() {
    this._myBookingService.onHandleCancel(this._pnr, () => this.closeModal());
  }

  clickChange() {
    this._myBookingService.onHandleChange(this._pnr, () => this.closeModal());
  }

  // 1.3 予約一覧表示
  get isDisplayReservationList(): boolean {
    return this.storeCurrentValue.isDisplayReservationList === true;
  }

  // 1.3 領収書
  get isReceipt(): boolean {
    return this._pnr?.orderEligibilities?.receipt?.isEligibleToForward ?? false;
  }
  // 1.3 搭乗者情報登録
  get isPassengerInformation(): boolean {
    return this._pnr?.orderEligibilities?.travelerInput?.isEligibleToForward ?? false;
  }
  // 1.3 解約
  get isCancel(): boolean {
    return this._pnr?.orderEligibilities?.cancel?.isEligibleToForward ?? false;
  }
  // 1.3 変更
  get isChange(): boolean {
    /** 提携：加えて下記条件を満たす場合も表示させる。
     * PNR情報取得API応答.data.orderEligibilities.change.isEligibleToForward = false、かつ以下すべての条件に一致する
      *  ユーザ共通情報.操作オフィスコードがWEB社用オフィスリスト(※1)に含まれる
      *  PNR情報取得API応答.data.orderEligibilities.eligibilityLevel.level = “2”
      *  PNR情報取得API応答.data.orderEligibilities.eligibilityLevel.restrictedReasons[]が”staffBooking”のみである。
    */
    return (
      (this._pnr?.orderEligibilities?.change?.isEligibleToForward ?? false)
      || (
        !(this._pnr?.orderEligibilities?.change?.isEligibleToForward ?? false)
        && this._myBookingService.getIsReservationChangeShowForStaff(this._pnr?.orderEligibilities?.eligibilityLevel!)
      )
    );
  }
}
